import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { NavigationContext } from '../../context/NavigationContext';
import { toast } from 'react-toastify';

interface TravelbingoEntry {
    id: number;
    createdAt: string;
    name: string;
    filename: string;
    status: string;
}

const TravelbingoEvaluations: React.FC = () => {
    const authContext = useContext(AuthContext);
    const navigationContext = useContext(NavigationContext);
    const [entries, setEntries] = useState<TravelbingoEntry[]>([]);
    const [loading, setLoading] = useState(true);

    if (!authContext) {
        throw new Error('TravelbingoEvaluations must be used within an AuthProvider');
    }

    if (!navigationContext) {
        throw new Error('TravelbingoEvaluations must be used within a NavigationProvider');
    }

    const { token } = authContext;
    const { navigateTo } = navigationContext;

    useEffect(() => {
        fetchEvaluations();
    }, []);

    const fetchEvaluations = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/travelbingo', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Fehler beim Laden der Auswertungen');
            }

            const data: TravelbingoEntry[] = await response.json();
            setEntries(data);
        } catch (error) {
            console.error('Error fetching travelbingo evaluations:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der Auswertungen');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (id: number) => {
        navigateTo(`/travelbingo/evaluations/${id}`);
    };

    if (loading) {
        return (
            <div style={{ padding: '20px' }}>
                <h1>Vorherige Auswertungen</h1>
                <p>Wird geladen...</p>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px' }}>
            <h1>Vorherige Auswertungen</h1>
            
            {entries.length === 0 ? (
                <p>Keine Auswertungen vorhanden.</p>
            ) : (
                <div style={{ marginTop: '20px' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Name</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Erstellt am</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Aktion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry) => (
                                <tr 
                                    key={entry.id} 
                                    style={{ borderBottom: '1px solid #dee2e6' }}
                                >
                                    <td style={{ padding: '12px' }}>{entry.name}</td>
                                    <td style={{ padding: '12px' }}>
                                        {new Date(entry.createdAt).toLocaleString('de-DE', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <span style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: entry.status === 'new' ? '#d1ecf1' : '#d4edda',
                                            color: entry.status === 'new' ? '#0c5460' : '#155724',
                                            fontSize: '14px'
                                        }}>
                                            {entry.status}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <button
                                            onClick={() => handleViewDetails(entry.id)}
                                            style={{
                                                padding: '6px 12px',
                                                fontSize: '14px',
                                                backgroundColor: '#007bff',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
                                        >
                                            Details anzeigen
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TravelbingoEvaluations;
